import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap, Presentation } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { formatApiError } from "@/lib/api";
import { homeFor } from "@/components/RequireRole";
import { AuthShell, Field, inputCls, primaryBtn } from "@/components/AuthShell";

const DEMOS = [
  { role: "student", email: "maya@consciouslearn.app", password: "Student123!", label: "Demo student", icon: GraduationCap },
  { role: "teacher", email: "teacher@consciouslearn.app", password: "Teacher123!", label: "Demo teacher", icon: Presentation },
];

export default function Login() {
  const { login } = useProfile();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (em = email, pw = password) => {
    setBusy(true); setError("");
    try {
      const u = await login(em, pw);
      nav(homeFor(u.role, u.learner_type));
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Take a breath. Let's continue your learning journey.">
      <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-5" data-testid="login-form">
        <Field label="Email"><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="login-email-input" /></Field>
        <Field label="Password"><input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="login-password-input" /></Field>
        <Link to="/forgot-password" data-testid="login-forgot-link" className="block -mt-2 text-right text-sm text-[#C87D53]">Forgot password?</Link>
        {error && <p className="text-sm text-red-700 bg-red-50 rounded-xl px-4 py-3" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={busy} className={primaryBtn} data-testid="login-submit-btn">{busy ? "Signing in…" : "Log in"}</button>
      </form>
      <div className="mt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-stone-500">Or explore instantly</p>
        <div className="grid grid-cols-2 gap-3 mt-3">
          {DEMOS.map(({ role, email: em, password: pw, label, icon: Icon }) => (
            <button key={role} type="button" disabled={busy} onClick={() => submit(em, pw)} data-testid={`demo-login-${role}-btn`}
              className="flex items-center gap-2 px-4 py-3 rounded-2xl border border-[#E6DEC3] bg-[#F7F3EB] hover:border-[#2C4A3E] text-sm transition-colors">
              <Icon className="w-4 h-4 text-[#C87D53]" /> {label}
            </button>
          ))}
        </div>
      </div>
      <Link to="/kids" data-testid="login-kids-link" className="mt-6 block text-center py-3 rounded-full border border-[#E6DEC3] bg-white text-[#2C4A3E] hover:border-[#2C4A3E]">Kids: sign in with pictures</Link>
      <p className="mt-8 text-sm text-stone-500">New here? <Link to="/register" className="text-[#C87D53] font-medium" data-testid="login-to-register-link">Create an account</Link></p>
    </AuthShell>
  );
}
